// MyReceipts — donor portal receipts catalog page (β.4 / CHG-1503).
//
// Consumes GET /api/v1/my-giving/receipts (CHG-1513 retarget; CHG-2947
// server-side paging) for the catalog + POST /api/v1/my-giving/receipts/
// {id}/reissue (CHG-2949, Slice G / DP-R4) for per-receipt PDF
// regeneration.
//
// Brief columns: donation_date, amount, designation, receipt_pdf_link.
//   - donation_date → issued_at (the receipt issuance date)
//   - amount        → amount_minor_units + currency
//   - designation   → PortalReceiptRead does not carry designation_name;
//                     CHG-1481 didn't expose it. Substituted org_name as
//                     the cross-org scope label (donors care about org
//                     first; designation is admin-internal). Reconciliation
//                     note in CHG-1503's registry entry — a follow-up
//                     could extend PortalReceiptRead with designation_name
//                     if product wants it.
//   - receipt_pdf   → custom column (CHG-2949 states): "ready" = direct
//                     Download link; missing/failed = "Regenerate
//                     document" action; "regenerating" = pending label +
//                     interval refetch of the list until pdf_url
//                     repopulates. Voided/partially-refunded receipts
//                     keep their Download link AND offer Regenerate —
//                     the artifact may predate the refund (stale
//                     full-value document); the server force-regenerates.
//
// One-off receipts vs annual statements: this page is per-GIFT tax
// receipts only. Annual giving statements live on the v1 MyReceipts
// AnnualReportWidget (and /my-giving/annual-report/*) — do not conflate.

import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ListPage, type ListPageConfig } from "../components/composition/ListPage";
import { useFormatters } from "../contexts/IntlContext";
import { meReceiptsApi, type MyReceipt } from "../api/me-receipts";

function organizationLabel(row: MyReceipt): string {
  return row.org_name ?? "—";
}

// CHG-2934 (DP-R13.3) — tax-validity badge + refunded caption. The backend
// amount_minor_units is already the NET deductible figure (CHG-2912), so a
// voided receipt reads $0 — this badge explains WHY instead of leaving an
// unexplained reduced amount. Component (not a bare render fn) so it can use
// the useFormatters hook for the refunded caption.
const RECEIPT_STATUS_TONE: Record<string, { bg: string; fg: string; label: string }> = {
  // Tokens match DataTableCell's LIFECYCLE_TONE conventions (tokens.css).
  valid: {
    bg: "var(--color-success-tint)",
    fg: "var(--color-success)",
    label: "Valid",
  },
  voided: {
    bg: "var(--color-error-tint)",
    fg: "var(--color-error)",
    label: "Voided",
  },
  partially_refunded: {
    bg: "var(--color-warning-tint)",
    fg: "var(--color-warning)",
    label: "Partially refunded",
  },
};

function ReceiptStatusCell({ receipt }: { receipt: MyReceipt }) {
  const fmt = useFormatters();
  const tone = RECEIPT_STATUS_TONE[receipt.status] ?? RECEIPT_STATUS_TONE.valid;
  const refunded = receipt.refunded_amount_minor_units ?? 0;
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          alignSelf: "flex-start",
          height: 22,
          padding: "0 8px",
          fontSize: 11,
          fontWeight: 600,
          background: tone.bg,
          color: tone.fg,
          borderRadius: 9999,
        }}
      >
        {tone.label}
      </span>
      {refunded > 0 ? (
        <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
          {fmt.formatCurrency(refunded, receipt.currency)} refunded
        </span>
      ) : null}
    </span>
  );
}

function statusColumn(row: MyReceipt): ReactNode {
  return <ReceiptStatusCell receipt={row} />;
}

const RECEIPTS_QUERY_KEY = ["me", "receipts", "list"] as const;
const REGENERATE_POLL_MS = 4_000;

function ReceiptPdfCell({ receipt }: { receipt: MyReceipt }) {
  // CHG-2949 (DP-R4 / §8 decision 4) — the donor-facing reissue endpoint
  // exists now (POST /my-giving/receipts/{id}/reissue); the CHG-1513
  // "endpoint is gone" label state is retired. States: ready /
  // regenerating / missing / failed (+ refund-stale regenerate).
  //
  // `requested` survives list refetches (same component type/position →
  // React preserves the cell instance; ListPage only remounts rows on
  // page/sort changes, where falling back to the plain missing state is
  // an acceptable degradation — the next refetch shows the fresh PDF).
  const queryClient = useQueryClient();
  const [requested, setRequested] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const refundAffected =
    receipt.status === "voided" || receipt.status === "partially_refunded";
  const regenerating = requested && !receipt.pdf_url;

  // Fresh artifact arrived → leave the regenerating state.
  useEffect(() => {
    if (receipt.pdf_url) setRequested(false);
  }, [receipt.pdf_url]);

  // Poll-by-refetch while a render is in flight: invalidate the list
  // query on an interval until pdf_url repopulates (simple refetch-on-
  // interval per the program slice; the render typically lands in
  // seconds). The interval dies with the state flip / cell unmount.
  useEffect(() => {
    if (!regenerating) return;
    const t = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: RECEIPTS_QUERY_KEY });
    }, REGENERATE_POLL_MS);
    return () => clearInterval(t);
  }, [regenerating, queryClient]);

  const reissue = useMutation({
    mutationFn: () => meReceiptsApi.reissueMyReceiptPdf(receipt.id),
    onSuccess: (resp) => {
      setErrorMsg(null);
      if (resp.status === "regenerating") {
        setRequested(true);
      }
      void queryClient.invalidateQueries({ queryKey: RECEIPTS_QUERY_KEY });
    },
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setErrorMsg(
        status === 429
          ? "Too many regeneration requests today — try again tomorrow."
          : "Could not regenerate — try again.",
      );
    },
  });

  const regenerateButton = (label: string) => (
    <button
      type="button"
      onClick={() => reissue.mutate()}
      disabled={reissue.isPending}
      style={{
        color: "var(--accent, #0066cc)",
        background: "none",
        border: "none",
        padding: 0,
        font: "inherit",
        fontSize: 13,
        cursor: reissue.isPending ? "wait" : "pointer",
      }}
    >
      {reissue.isPending ? "Requesting…" : label}
    </button>
  );

  const errorCaption = errorMsg ? (
    <span style={{ fontSize: 11, color: "var(--color-error)" }}>{errorMsg}</span>
  ) : null;

  if (regenerating || (reissue.isPending && !receipt.pdf_url)) {
    return (
      <span style={{ color: "var(--muted, #666)", fontSize: 13 }}>
        Regenerating…
      </span>
    );
  }

  if (receipt.pdf_url) {
    return (
      <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
        <a
          href={receipt.pdf_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "var(--accent, #0066cc)" }}
        >
          Download PDF
        </a>
        {refundAffected ? (
          // Refund-affected artifact may predate the refund banners —
          // offer a fresh render (server force-regenerates; CHG-2927
          // binding item).
          regenerateButton("Regenerate document")
        ) : null}
        {errorCaption}
      </span>
    );
  }

  // Missing vs failed render (no PDF either way; both recoverable now).
  const failed = receipt.delivery_status === "failed";
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
      <span style={{ color: "var(--muted, #666)", fontSize: 13 }}>
        {failed ? "Document failed to generate" : "PDF unavailable"}
      </span>
      {regenerateButton("Regenerate document")}
      {errorCaption}
    </span>
  );
}

function pdfColumn(row: MyReceipt): ReactNode {
  return <ReceiptPdfCell receipt={row} />;
}

const myReceiptsListConfig: ListPageConfig<MyReceipt> = {
  title: "My receipts",
  subtitle:
    "Tax receipts for every gift you've made. Download the PDF or re-issue a fresh copy.",
  queryKey: ["me", "receipts", "list"] as const,
  queryFn: ({ page, pageSize, sortField, sortDirection, search }) =>
    meReceiptsApi.listMyReceipts({ page, pageSize, sortField, sortDirection, search }),
  defaultSort: { field: "issued_at", direction: "desc" },
  pageSize: 25,
  emptyState: {
    title: "No receipts yet",
    subtitle:
      "When you complete a donation, your receipt and download link appear here automatically.",
  },
  columns: [
    {
      field: "issued_at",
      label: "Issued",
      type: "relative-date",
      sortable: true,
    },
    {
      // CHG-2912/CHG-2934 — NET deductible amount (refund-adjusted by the
      // backend); the Status column explains any reduction.
      field: "amount_minor_units",
      label: "Amount",
      type: "currency",
      currencyField: "currency",
      sortable: true,
    },
    {
      // CHG-2934 (DP-R13.3) — voided / partially-refunded badge + caption.
      field: "status",
      label: "Status",
      type: "custom",
      render: statusColumn,
    },
    {
      field: "org_name",
      label: "Organization",
      type: "custom",
      render: organizationLabel,
    },
    {
      field: "receipt_number",
      label: "Receipt #",
      type: "text",
    },
    {
      field: "pdf_url",
      label: "PDF",
      type: "custom",
      render: pdfColumn,
    },
  ],
};

export function MyReceipts() {
  return <ListPage config={myReceiptsListConfig} />;
}
